import React, { FC } from 'react';

import { type Action } from '~/lib/authz/eval';

import { BankAccountSelector } from './BankAccountSelector';
import { CounterpartySelector } from './CounterpartySelector';
import { type SequenceRow, TransferSequence } from './TransferSequence';

/** A controlled account field — maps cleanly onto a react-hook-form Controller's `field`. */
interface AccountField {
  value: string;
  onChange: (id: string) => void;
  onBlur: () => void;
  hasError?: boolean;
  /** Row label in the sequence (defaults per side). */
  label?: string;
  /** Helper copy shown under the label. */
  description?: string;
  /** `data-cy` hook applied to the field. */
  dataCy?: string;
}

export interface AccountsSequenceProps {
  bankAccount: AccountField & {
    /** Permission gating which accounts are selectable. */
    permission?: Action['permission'];
    /** Tooltip shown on accounts the user can't use. */
    unauthorizedText?: string;
    /** Transfer amount (cents); flags insufficient funds when the bank account is the origin. */
    amount?: number;
  };
  counterparty: AccountField & {
    /** Counterparty kind for the "New counterparty" modal (e.g. "ach", "wire"). */
    counterpartyType: string;
  };
  /**
   * Which side the money originates from. That side renders on top (the sequence
   * reads origin → destination) and, when it's the bank account, its available
   * balance is checked against the amount.
   */
  originatesFrom: 'bankAccount' | 'counterparty';
}

/**
 * Lays out a bank account and a counterparty as a direction-ordered, maps-style
 * sequence. Deliberately presentational and form-library-agnostic: the caller
 * wires each field (e.g. via react-hook-form) and supplies the transfer
 * direction, so any transfer form can reuse it. See AchAccountsSequence for a
 * concrete example.
 */
export const AccountsSequence: FC<AccountsSequenceProps> = ({ bankAccount, counterparty, originatesFrom }) => {
  const bankIsSource = originatesFrom === 'bankAccount';

  const bankRow: SequenceRow = {
    key: 'bankAccount',
    label: bankAccount.label ?? 'Bank Account',
    description: bankAccount.description,
    required: true,
    selected: Boolean(bankAccount.value),
    node: (
      <BankAccountSelector
        value={bankAccount.value}
        onChange={bankAccount.onChange}
        onBlur={bankAccount.onBlur}
        hasError={bankAccount.hasError}
        permission={bankAccount.permission}
        unauthorizedText={bankAccount.unauthorizedText}
        amount={bankAccount.amount}
        isSource={bankIsSource}
        dataCy={bankAccount.dataCy}
      />
    ),
  };

  const counterpartyRow: SequenceRow = {
    key: 'counterparty',
    label: counterparty.label ?? 'Counterparty',
    description: counterparty.description,
    required: true,
    selected: Boolean(counterparty.value),
    node: (
      <CounterpartySelector
        value={counterparty.value}
        onChange={counterparty.onChange}
        onBlur={counterparty.onBlur}
        hasError={counterparty.hasError}
        counterpartyType={counterparty.counterpartyType}
        dataCy={counterparty.dataCy}
      />
    ),
  };

  // Order encodes direction: the money origin sits on top.
  const rows = bankIsSource ? [bankRow, counterpartyRow] : [counterpartyRow, bankRow];

  return <TransferSequence rows={rows} />;
};
